import React, { useState, useEffect, useRef, useMemo } from 'react'

import { createAnimation, IonFooter, useIonViewDidEnter, useIonViewWillLeave } from '@ionic/react'
import { ionHistory } from '../../navigation'
import { Button, Item, Label, List, Popover, PopoverContent, Text, Grid, GridRow, GridCol } from '@mytours/components'
import ScannerModal from '../../scanner/components/ScannerModal'
import ScannerIcon from 'mobile_web/modules/scanner/components/parts/ScannerIcon'
import { FlickitySlider } from './Flickity'
import AccessModal from 'mobile_web/modules/common/components/access-modal'
import { MapControls } from '../../map/types'
import { GetCollectionsShowResponse, GetCollectionsShowResponseMapLayers, MapRoute } from 'mytours-read-api-typescript'

import { CollectionItem, KeypadEntryStatus, ScreenSection, KeypadItem, Collection } from 'mytours-read-common'
import { ImageSizePreset } from '../../../common_lib/types'
import { generateImageUrl } from '../../../common_lib/utils'

import { addToHistoryState, getImageAlt } from '../../common/functions'
import { BoundDirection, CollectionItemsRouteParams } from '../../collection/types'

import {
	contentModalDownAfterStyle,
	contentModalUpAfterStyle,
	modalFooterDownAfterStyle,
	modalFooterUpAfterStyle,
	sliderModalDown,
	sliderModalDownAfterStyle,
	sliderModalFooterDown,
	sliderModalFooterUp,
	sliderModalUp,
	sliderModalUpAfterStyle,
} from '../constants/animation'
import { ToggleMenu } from 'mobile_web/modules/common/components/header/ToggleMenu'
import { getDirMode, isRTL } from 'mobile_web/modules/platform'

import strings from '../../../strings'

import classNames from 'classnames'
import '../styles/mapActionsLayout.less'
import { getPageComponent } from 'mobile_web/modules/home/functions'
import Flickity from 'react-flickity-component'
import { checkAndremoveFullWidthClass } from '../functions'
import { withRouter } from 'react-router'
import { getMapQueryParams, replaceMapQueryParams } from 'mobile_web/modules/navigation/queryParams'
import MenuList from 'mobile_web/modules/common/components/menu/MenuList'
import usePrevious from 'common_lib/hooks/use-previous'
import MapComponent from '../components/MapComponent'

const LayerTypeEnum = GetCollectionsShowResponseMapLayers.LayerTypeEnum
type StaticMapLayerType = GetCollectionsShowResponseMapLayers.LayerTypeEnum.Static
type MapboxLayerType = GetCollectionsShowResponseMapLayers.LayerTypeEnum.Mapbox
type GeoreferencedLayerType = GetCollectionsShowResponseMapLayers.LayerTypeEnum.Georeferenced

interface Props {
	accountId?: number
	keypadEntryItems?: KeypadItem[]
	items: CollectionItem[]
	keypadEntryLoading?: boolean
	keypadEntryStatus?: KeypadEntryStatus
	currentItemIndex: number
	keypadEnabled?: boolean
	isMapLayout: boolean
	details: Collection
	boundAll?: boolean
	onExit?: () => void
	onGoBack?: () => void
	onBackAction?: (params: any) => void
	onCloseMap?: () => void
	setCurrentPage?: (itemIndex: number) => void
	onItemNavigate?: (item: CollectionItem) => void
	// The host shows a chosen item itself, usually closing the map; otherwise it opens in the details modal.
	onItemClicked?: (item: CollectionItem) => void
	renderToggleMenuContent?: () => JSX.Element
	itemType?: MapRoute.LinkedTypeEnum
	onSelectItemFromMenu: (id: number) => void
}

const MapLayout: React.FC<Props & CollectionItemsRouteParams> = props => {
	const modalRef = useRef()
	const modalContentRef = useRef()
	const modalFooterRef = useRef()
	const mapRef = useRef(null)
	const [mapReady, setMapReady] = useState<boolean>(false)
	const [sliderReady, setSliderReady] = useState<boolean>(false)
	const [renderKey, setRenderKey] = useState<number>(0)

	const [activeItem, setActiveItem] = useState<CollectionItem | ScreenSection>(null)
	const [activeItemIndex, setActiveItemIndex] = useState<number>(props.currentItemIndex)

	const [showFloorPopover, setShowFloorPopover] = useState<boolean>(false)
	const [floorPopoverEvent, setFloorPopoverEvent] = useState<React.SyntheticEvent | null>(null)
	const [selectedMapLayerId, setSelectedMapLayerId] = useState<number>(null)

	const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false)
	const [showIframeInModal, setShowIframeInModal] = useState<boolean>(false)

	const [showToolbar, setShowToolbar] = useState<boolean>(true)
	const [showScanner, setShowScanner] = useState<boolean>(false)

	const [showMyPosition, setShowMyPosition] = useState<boolean>(false)

	const [mapDetailsOpenedByHash, setMapDetailsOpenedByHash] = useState<boolean>(false)

	const activeItemId: number = (ionHistory?.location?.state as any)?.activeMenuId || null
	const isMenuAction = (ionHistory?.location?.state as any)?.action === 'menu'

	const prevKeypadEntryStatus = usePrevious<KeypadEntryStatus>(props.keypadEntryStatus)

	const flkty: React.MutableRefObject<Flickity> = useRef(null)
	const isCollectionTour = useMemo<boolean>(
		() => props.details?.subtype === GetCollectionsShowResponse.SubtypeEnum.CollectionTour,
		[props.details?.subtype],
	)

	const mapLayerIds = useMemo<number[]>(
		() => props.details.map?.layers?.map(layer => layer.id) || [],
		[props.details.map?.layers],
	)

	const mapLayers = useMemo<GetCollectionsShowResponseMapLayers[]>(
		() => props.details?.map?.layers || [],
		[props.details?.map?.layers],
	)

	const mapType = useMemo<MapboxLayerType | StaticMapLayerType | GeoreferencedLayerType | undefined>(() => {
		for (const layer of mapLayers) {
			if (
				layer.layer_type === LayerTypeEnum.Mapbox ||
				layer.layer_type === LayerTypeEnum.Static ||
				layer.layer_type === LayerTypeEnum.Georeferenced
			) {
				return layer.layer_type
			}
		}

		return
	}, [mapLayers])

	const items = useMemo<CollectionItem[]>(
		() =>
			mapType === LayerTypeEnum.Static || mapType === LayerTypeEnum.Georeferenced
				? props.items.filter(item => item.map_layer_id == null || mapLayerIds.includes(item.map_layer_id))
				: props.items,
		[props.items, mapType],
	)

	const sliderItems = useMemo<CollectionItem[]>(() => items.filter(item => item.lat && item.lng), [items])

	const getSlideIndexFromItemIndex = (itemIndex: number) => {
		const slideIndex = sliderItems.indexOf(items[itemIndex])
		if (slideIndex !== -1) {
			return slideIndex
		} else {
			return 0
		}
	}

	const activeSlideIndex = getSlideIndexFromItemIndex(activeItemIndex > 0 ? activeItemIndex : 0)

	const getItemIndexFromSlideIndex = (slideIndex: number) => items.indexOf(sliderItems[slideIndex])

	const setActiveItemData = (activeItemParam: CollectionItem | ScreenSection, activeItemIndexParam: number) => {
		setActiveItem(activeItemParam)
		setActiveItemIndex(activeItemIndexParam)
	}

	const getMapLayerId = (itemIndex?: number, id?: number): number | null => {
		const index = itemIndex || activeItemIndex

		const item = items.find((el: CollectionItem) => items[index] && el.map_layer_id === items[index].map_layer_id)

		const defaultValue = item?.map_layer_id || (mapLayers?.length > 0 && mapLayers?.[0]?.id)

		let selectedMapLayerId = defaultValue

		if (id) {
			const layer = mapLayers.find(layer => layer.id === id)
			selectedMapLayerId = layer?.id ?? defaultValue
		}

		return selectedMapLayerId
	}

	const setActiveItemState = (activeItemId: number) => {
		addToHistoryState({ activeMenuId: activeItemId, action: 'slider' })
	}

	const updateActiveItem = (activeItemParam: CollectionItem | ScreenSection, itemIndexParam: number): void => {
		setActiveItemData(activeItemParam, itemIndexParam)

		if (mapType === LayerTypeEnum.Static || mapType === LayerTypeEnum.Georeferenced) {
			setSelectedMapLayerId(getMapLayerId(itemIndexParam))
		}

		setActiveItemState(activeItemParam?.id)
	}

	const changeStopByItemId = (itemId: number) => {
		const tourStopIndex = items.findIndex(item => item.item_id === itemId)
		if (tourStopIndex > -1) {
			updateActiveItem(items[tourStopIndex], tourStopIndex)
			setShowScanner(false)
		}
	}

	const selectSliderItemByIndex = (itemIndex: number) => {
		if (itemIndex > -1 && flkty?.current?.selectedIndex !== getSlideIndexFromItemIndex(itemIndex)) {
			flkty?.current?.select(getSlideIndexFromItemIndex(itemIndex))
		}
	}

	function addFullWidthClass() {
		const currentIonPage = mapRef?.current?.closest('.ion-page')
		const currentHomeTabs = mapRef?.current?.closest('.home-tabs')

		currentIonPage?.classList.add('full-width')
		currentHomeTabs?.classList.add('full-width')
	}

	function removeFullWidthClass(currentIonPage: HTMLElement) {
		currentIonPage?.classList.remove('full-width')
		checkAndremoveFullWidthClass()
	}

	useEffect(() => {
		addFullWidthClass()
		const currentIonPage = mapRef?.current?.closest('.ion-page')
		return () => {
			removeFullWidthClass(currentIonPage)
		}
	}, [])

	useIonViewDidEnter(() => {
		addFullWidthClass()
		setRenderKey(Date.now())
	})
	useIonViewWillLeave(() => {
		const currentIonPage = mapRef?.current?.closest('.ion-page')
		removeFullWidthClass(currentIonPage)
	})

	// Reopen the details modal the URL records for a tour, once the map is ready and the modal element exists.
	useEffect(() => {
		// A host that takes chosen items itself (onItemClicked) has no details modal to restore.
		if (!activeItem || !isCollectionTour || props.onItemClicked) return

		const { showDetailsModal: showDetailsModalParam, showDetailsModalExist } = getMapQueryParams()
		// set opened by hash to true if 'show-details-modal' does not exist in params
		// because when user clicks on slide and 'show-details-modal' appears in search modal
		// opens two times and animation runs two times
		if (!showDetailsModalExist) {
			setMapDetailsOpenedByHash(true)
		}

		if (mapReady && !mapDetailsOpenedByHash && showDetailsModalParam && showDetailsModalExist && !showDetailsModal) {
			setMapDetailsOpenedByHash(true)
			toggleMapDetailsModal()
		}
	}, [ionHistory?.location.hash, ionHistory?.location.search, activeItem, mapReady])

	// The same restore for a list, whose URL also names the item to open.
	useEffect(() => {
		if (isCollectionTour) return

		const { showDetailsModal: showDetailsModalParam, collectionItemId, showDetailsModalExist } = getMapQueryParams()

		const collectionItemIndex = items.findIndex(item => item.id === Number(collectionItemId))
		if (collectionItemIndex === -1) {
			return
		}

		// Look prev useEffect for description of this
		if (!showDetailsModalExist) {
			setMapDetailsOpenedByHash(true)
		}

		if (mapReady && !mapDetailsOpenedByHash && showDetailsModalParam && showDetailsModalExist && !showDetailsModal) {
			setMapDetailsOpenedByHash(true)
			setActiveItemData(items[collectionItemIndex], collectionItemIndex)
			selectSliderItemByIndex(collectionItemIndex)
			toggleMapDetailsModal()
		}
	}, [ionHistory?.location.hash, ionHistory?.location.search, props.items, mapReady])

	/**
	 * Change active item when item id changed in the url
	 */
	const { collectionItemId } = getMapQueryParams()
	const prevParamsItemId = usePrevious(props.match.params?.itemId)
	const prevCollectionItemId = usePrevious(collectionItemId)

	useEffect(() => {
		if (prevParamsItemId !== props.match.params?.itemId || prevCollectionItemId !== collectionItemId) {
			const collectionItemIndex = items.findIndex(
				item => item.id === Number(props.match.params?.itemId || collectionItemId),
			)

			if (collectionItemIndex === -1) {
				return
			}

			setActiveItemData(items[collectionItemIndex], collectionItemIndex)

			selectSliderItemByIndex(collectionItemIndex)

			if (!showDetailsModal && !isCollectionTour) {
				toggleMapDetailsModal()
			}
		}
	}, [props.match.params?.itemId, collectionItemId])

	useEffect(() => {
		if (mapLayers.length > 0 || !items || items.length === 0) {
			return
		}

		let activeItem = null

		if (items[activeItemIndex]) {
			activeItem = items[activeItemIndex]
		}

		setActiveItemData(activeItem, activeItemIndex)
	}, [items])

	useEffect(() => {
		const { keypadEntryStatus, keypadEntryItems, keypadEntryLoading } = props
		if (
			prevKeypadEntryStatus === KeypadEntryStatus.Processing &&
			!keypadEntryStatus &&
			keypadEntryItems.length === 1 &&
			!keypadEntryLoading
		) {
			changeStopByItemId(keypadEntryItems[0].id)
		}
	}, [props.keypadEntryStatus])

	useEffect(() => {
		const { isMapLayout } = props
		const currentId = activeItemId || items?.[activeItemIndex || 0]?.id
		const showDetailModal = currentId > -1 && isMapLayout && isMenuAction && !showDetailsModal
		if (showDetailModal) {
			setTimeout(() => {
				setActiveItemState(currentId)
				toggleMapDetailsModal()
			}, 0)
		}
	}, [activeItemIndex, activeItemId])

	useEffect(() => {
		if (!showDetailsModal && activeItemIndex != null) {
			selectSliderItemByIndex(activeItemIndex)
		}
	}, [activeItemIndex])

	useEffect(() => {
		if (!mapLayers) {
			return
		}

		setSelectedMapLayerId(getMapLayerId())
	}, [mapLayers])

	const toggleScannerModal = (itemId?: number) => {
		setShowScanner(!showScanner)
		if (itemId) {
			changeStopByItemId(itemId)
		}
	}

	const onShowPopover = event => {
		event.persist()

		setFloorPopoverEvent(event)
		setShowFloorPopover(true)
	}

	const getLayerTitle = (layerId?: number): string => {
		if (!layerId) {
			layerId = selectedMapLayerId || getMapLayerId()
		}

		const activeLayer = mapLayers.find(layer => layer.id === layerId)
		return activeLayer && activeLayer.title
	}

	const getActiveButtonsLength = () => {
		const { details = {} as Collection } = props
		const { map = {} } = details

		const activeItems = [
			details.keypad_enabled || details.qr_enabled,
			mapType === LayerTypeEnum.Mapbox,
			activeItem && map && map.directions_enabled,
			(mapType === LayerTypeEnum.Static || mapType === LayerTypeEnum.Georeferenced) && mapLayers.length > 1,
		]

		const activeCount = activeItems.filter((item: boolean) => !!item)?.length || 0
		return activeCount + 2 /* 2 buttons always present */
	}

	const renderMapControls = ({ showAllBounds, getMyPosition, showInAppDirections }: MapControls): JSX.Element => {
		const { details = {} as Collection, onCloseMap, onExit, onGoBack } = props
		const { map = {} } = details

		const hasInAppDirections = activeItem && map && map.directions_enabled

		let exitButtonTitle = ''
		if (onCloseMap) {
			exitButtonTitle = strings.close
		} else if (onExit) {
			exitButtonTitle = strings.tour_exit
		} else if (onGoBack) {
			exitButtonTitle = strings.accessibility_go_back
		}
		const exitAction = onCloseMap || onExit || onGoBack

		return (
			<GridRow className="map-controls-container" dir={getDirMode()}>
				{!!exitAction && (
					<div className="map-controls-row map-controls-exit">
						<Button
							variant="clear"
							aria-label={exitButtonTitle}
							className="map-controls-exit-button"
							onClick={exitAction}
						>
							<i className={`fal fa-chevron-${getDirMode() === 'rtl' ? 'right' : 'left'}`} />
							<span>{exitButtonTitle}</span>
						</Button>
					</div>
				)}
				<div className="map-controls-row">
					{(details.keypad_enabled || details.qr_enabled) && (
						<Button variant="clear" onClick={() => toggleScannerModal()} aria-label={strings.scan_and_find}>
							<ScannerIcon
								keypadEnabled={details.keypad_enabled}
								qrEnabled={details.qr_enabled}
								searchEnabled={false}
							/>
						</Button>
					)}
					<Button variant="clear" onClick={showAllBounds} aria-label={strings.center_map}>
						<i className="fal fa-draw-square" />
					</Button>
					{mapType === LayerTypeEnum.Mapbox && (
						<>
							<Button variant="clear" onClick={() => setShowMyPosition(true)} aria-label={strings.my_location}>
								<i className="fal fa-location-arrow" />
							</Button>
							{showMyPosition && (
								<AccessModal
									types={['location']}
									onCheckDone={() => {
										getMyPosition()
										setShowMyPosition(false)
									}}
								/>
							)}
							{hasInAppDirections && (
								<Button variant="clear" onClick={() => showInAppDirections(true)} aria-label={strings.directions}>
									<i className="fal fa-directions" />
								</Button>
							)}
						</>
					)}
					{(mapType === LayerTypeEnum.Static || mapType === LayerTypeEnum.Georeferenced) && mapLayers.length > 1 && (
						<>
							<Button variant="clear" onClick={onShowPopover} aria-label={strings.accessibility_show_map_layers}>
								<i className="fal fa-layer-group" />
							</Button>
							{getLayerTitle() && (
								<Text variant="inherit" onClick={onShowPopover}>
									{getLayerTitle()}
								</Text>
							)}
						</>
					)}
				</div>
				<div className="map-controls-row map-controls-menu">
					<ToggleMenu
						collection={details}
						title={details?.title}
						content={<MenuList list={items} activePageId={activeItemId} navigateToItem={props.onSelectItemFromMenu} />}
						fullScreen={true}
					/>
				</div>
			</GridRow>
		)
	}

	const ltrLeft = getDirMode() === 'rtl' ? 'right' : 'left'
	const ltrRight = getDirMode() === 'rtl' ? 'left' : 'right'

	const renderFooterControls = () => (
		<>
			<Button
				aria-label={strings.tour_previous}
				variant="clear"
				className="nav-action"
				onClick={() => changeScreenAction('prev')}
			>
				<i className={`fal fa-chevron-${ltrLeft}`} />
				<Text variant="inherit" className="nav-action-label">
					{strings.tour_previous}
				</Text>
			</Button>
			<Button
				variant="clear"
				className="expand-button -with-text"
				onClick={() => {
					replaceMapQueryParams(!showDetailsModal, props.details?.subtype, activeItemId)
					toggleMapDetailsModal()
				}}
			>
				<Text variant="inherit" className="expand-button-label">
					{strings.back_to_map}
				</Text>
				<i className="fal fa-chevron-down" />
			</Button>
			<Button
				aria-label={strings.tour_next}
				variant="clear"
				className="nav-action"
				onClick={() => changeScreenAction('next')}
			>
				<Text variant="inherit" className="nav-action-label">
					{strings.tour_next}
				</Text>
				<i className={`fal fa-chevron-${ltrRight}`} />
			</Button>
		</>
	)

	const changeScreenAction = (direction: BoundDirection) => {
		const { onItemNavigate, onItemClicked, setCurrentPage } = props

		let index

		if (direction == 'prev') {
			index = activeItemIndex - 1 > -1 ? activeItemIndex - 1 : items.length - 1
		} else if (direction == 'next') {
			index = activeItemIndex + 1 < items.length ? activeItemIndex + 1 : 0
		}

		if (onItemClicked) {
			toggleMapDetailsModal()
			setTimeout(() => onItemClicked(items[index]), 0)
		} else {
			onItemNavigate?.(items[index])
			setCurrentPage?.(index)

			setActiveItemData(items[index], index)

			selectSliderItemByIndex(index)
		}
	}

	const updateMapLayerId = (id?: number) => {
		setSelectedMapLayerId(getMapLayerId(undefined, id))
		hidePopover()
	}

	const openMapLayer = (id: number) => {
		updateMapLayerId(id)
	}

	const hidePopover = () => setShowFloorPopover(false)

	// Ionic's ios popover snapped to 5px from a viewport edge once it came within 30px of it.
	const snapToEdge = ({ positioner }: { positioner: { width: number } }) => {
		const target = floorPopoverEvent?.target as Element | undefined
		if (!target) return 0
		const { left, width } = target.getBoundingClientRect()
		const x = left + width / 2 - positioner.width / 2
		const max = window.innerWidth - positioner.width - 5
		const shift = x < 30 ? 5 - x : x > max - 25 ? max - x : 0
		return isRTL() ? -shift : shift
	}

	const renderPopover = (): JSX.Element => (
		<Popover
			open={showFloorPopover}
			onOpenChange={open => !open && hidePopover()}
			onOpenChangeComplete={open => !open && setFloorPopoverEvent(null)}
			modal
		>
			<PopoverContent
				className="floor-popover"
				backdropClassName="floor-popover-backdrop"
				anchor={floorPopoverEvent?.target as Element | undefined}
				sideOffset={10}
				alignOffset={snapToEdge}
				arrowPadding={0}
				backdrop
				animated
				arrow
			>
				<List variant="ios">
					{mapLayers.map((item, index) => (
						<Item key={index} onClick={() => openMapLayer(item.id as number)} aria-label={getLayerTitle(item.id)}>
							<Label as="span">{getLayerTitle(item.id)}</Label>
						</Item>
					))}
				</List>
			</PopoverContent>
		</Popover>
	)

	const renderScanner = () => {
		const { details = {} as Collection } = props

		return (
			<ScannerModal
				qrEnabled={details.qr_enabled}
				keypadEnabled={details.keypad_enabled}
				showScanner={showScanner}
				toggleScannerModal={toggleScannerModal}
				itemId={details.id}
			/>
		)
	}

	const showIframe = (show: boolean) => {
		if (show) {
			setTimeout(() => {
				setShowIframeInModal(true)
			}, 1000) // Use timeout to prevent animation laggy
		}
	}

	const toggleMapDetailsModal = () => {
		// Toggling before the modal mounts animates nothing and hides the toolbar for a modal that never appears.
		if (!modalRef.current) return

		setShowDetailsModal(!showDetailsModal)
		showIframe(!showDetailsModal)
		triggerAnimation(!showDetailsModal ? 'up' : 'down')
	}

	const triggerAnimation = (type: 'up' | 'down') => {
		if (type === 'down') {
			contentAnimation(type)
			footerAnimation(type)
		} else {
			setShowDetailsModal(!showDetailsModal)
			showIframe(!showDetailsModal)
			triggerModalAnimation(type)
		}
	}

	const triggerModalAnimation = (type: 'up' | 'down') => {
		const mapClientHeight = mapRef?.current?.clientHeight
		const maxHeight = `calc(${mapClientHeight}px - 3rem)`
		const keyframes =
			type === 'down' ? [...sliderModalDown(isRTL(), maxHeight)] : [...sliderModalUp(isRTL(), maxHeight)]
		const afterStyles =
			type === 'down' ? sliderModalDownAfterStyle : { ...sliderModalUpAfterStyle, height: `${mapClientHeight}px` }

		if (modalRef) {
			const animation = createAnimation()
				.addElement(modalRef.current)
				.duration(400)
				.easing('ease-in-out')
				.keyframes(keyframes)
				.afterStyles(afterStyles)
				.onFinish(() => {
					setShowToolbar(showDetailsModal)
					if (type === 'up') contentAnimation(type)
					animation?.destroy?.()
				})
			animation?.play()
		}
	}

	const contentAnimation = (type: 'up' | 'down') => {
		const afterStyles = type === 'down' ? contentModalDownAfterStyle : contentModalUpAfterStyle

		if (modalContentRef?.current) {
			const animation = createAnimation()
				.addElement(modalContentRef.current)
				.afterStyles(afterStyles)
				.duration(400)
				.easing('ease-in-out')
				.onFinish(() => {
					if (type === 'up') footerAnimation(type)
					animation?.destroy?.()
				})

			animation?.play()
		}
	}

	const footerAnimation = (type: 'up' | 'down') => {
		const keyframes = type === 'up' ? [...sliderModalFooterUp] : [...sliderModalFooterDown]
		const afterStyles = showDetailsModal ? modalFooterUpAfterStyle : modalFooterDownAfterStyle

		if (modalFooterRef) {
			const animation = createAnimation()
				.addElement(modalFooterRef.current)
				.afterStyles(afterStyles)
				.duration(400)
				.easing('ease-in-out')
				.keyframes(keyframes)
				.onFinish(() => {
					if (type === 'down') triggerModalAnimation('down')
					animation?.destroy?.()
				})

			animation?.play()
		}
	}

	const onActiveSlideClick = (currentItem: CollectionItem, currentItemIndex: number) => {
		const { onItemNavigate, setCurrentPage, onItemClicked } = props

		setActiveItemData(currentItem, currentItemIndex)

		selectSliderItemByIndex(currentItemIndex)

		if (onItemClicked) {
			onItemClicked(currentItem)
		} else {
			toggleMapDetailsModal()
			if (onItemNavigate) onItemNavigate(currentItem)
			if (setCurrentPage && typeof currentItemIndex === 'number') setCurrentPage(currentItemIndex)
		}
	}

	const handleStaticClick = (slideIndex: number) => {
		// On a handoff the map closes — a details modal recorded in the URL would outlive it and reopen later.
		if (!props.onItemClicked) {
			replaceMapQueryParams(!showDetailsModal, props.details?.subtype, activeItemId)
		}
		onActiveSlideClick(sliderItems[slideIndex], getItemIndexFromSlideIndex(slideIndex))
	}

	const renderSliderItem = (item: CollectionItem, index: number) => {
		let imageUrl: string = undefined
		const image = item?.cover_image_grid || item?.cover_image
		if (image?.file) {
			const imageFocalPoint = image?.file?.focal_point
			const imageCrop = imageFocalPoint && (imageFocalPoint[0] !== 0.5 || imageFocalPoint[1] !== 0.5)
			imageUrl = generateImageUrl(image.file, {
				imageSizePreset: ImageSizePreset.Grid,
				crop: imageCrop,
				focalPoint: imageFocalPoint,
			})
		}

		return (
			<div className="slide-container" key={index}>
				<div className="content">
					{imageUrl && (
						<GridCol className="slide-image-col" size="auto">
							<img className="image" src={imageUrl} alt={getImageAlt(image)} />
						</GridCol>
					)}
					<div className="details">
						<p>{item.title}</p>
					</div>
					<Button variant="clear" className="expand-button" aria-label={strings.open_details}>
						<i className="fal fa-chevron-up" aria-hidden={true} />
					</Button>
				</div>
			</div>
		)
	}

	const renderToolbarItems = () =>
		sliderItems.map((item: CollectionItem, sliderIndex: number) => renderSliderItem(item, sliderIndex))

	const modalContent = () => {
		const currentItem = items[activeItemIndex]

		const content = getPageComponent(
			currentItem?.item_subtype,
			currentItem?.item_id,
			currentItem?.version,
			null,
			true,
			currentItem?.account_id,
		)

		return content
	}

	const renderModal = () => (
		<div className="map-details-modal" ref={modalRef}>
			<div ref={modalContentRef} className="map-details-modal-content">
				{showDetailsModal && showIframeInModal && modalContent()}
			</div>
			<IonFooter ref={modalFooterRef} className="map-details-modal-footer" dir={getDirMode()}>
				{renderFooterControls()}
			</IonFooter>
		</div>
	)

	const sliderTimerRef = useRef(null)

	const renderToolbarSlider = () => {
		const sliderProps = {
			options: { initialIndex: activeSlideIndex },
		}

		return (
			<Grid
				dir={getDirMode()}
				className={classNames('map-toolbar', {
					hide: !(!showDetailsModal && showToolbar),
				})}
			>
				<div className="toolbar-slider">
					<FlickitySlider
						sliderOptions={sliderProps}
						activeSlideIndex={activeSlideIndex}
						renderItems={renderToolbarItems}
						onChange={newSlideIndex => {
							if (sliderTimerRef.current) {
								clearTimeout(sliderTimerRef.current)
								sliderTimerRef.current = null
							}
							sliderTimerRef.current = setTimeout(() => {
								updateActiveItem(sliderItems[newSlideIndex], getItemIndexFromSlideIndex(newSlideIndex))
								sliderTimerRef.current = null
							}, 400) // Animation timeout
						}}
						flkty={flktyRef => {
							flkty.current = flktyRef
						}}
						onStaticClick={handleStaticClick}
					/>
				</div>
			</Grid>
		)
	}

	const onMapReady = () => {
		if (!mapReady || !sliderReady) {
			setMapReady(true)
			setTimeout(() => {
				// show slider after 1s when the map ready to prevent loading items duplication
				setSliderReady(true)
			}, 1000)
		}
	}

	const resizeFlkty = () => {
		flkty?.current?.resize()
	}

	const maxItemsClass = getActiveButtonsLength() > 4

	return (
		<>
			<GridRow ref={mapRef} className={classNames('map-row', { 'map-max-controls': maxItemsClass })}>
				<MapComponent
					accountId={props.accountId}
					items={items}
					details={props.details}
					currentItemIndex={props.currentItemIndex}
					boundAll={props.boundAll}
					renderMapControls={renderMapControls}
					selectedMapLayerId={selectedMapLayerId}
					onSetActiveItem={updateActiveItem}
					activeItemIndex={activeItemIndex}
					containerType={isCollectionTour ? 'tour' : 'list'}
					containerId={props.details?.id}
					onMapReady={onMapReady}
					mapLayers={mapLayers}
					mapType={mapType}
					onMapResize={resizeFlkty}
				/>
				{renderPopover()}
				{mapReady && renderModal()}
				{sliderReady && renderToolbarSlider()}
			</GridRow>
			{renderScanner()}
		</>
	)
}

export default withRouter(MapLayout)
